import { useMemo, useState } from 'react'
import { Download, Filter, Search, Star } from 'lucide-react'

import { Button } from '@/components'

type Certificate = {
  id: string
  childName: string
  title: string
  course: string
  completionDate: string
  finalGrade: string
  skills: string[]
  type: 'excellence' | 'achievement'
}

const CERTIFICATES: Certificate[] = [
  {
    id: '1',
    childName: 'Sarah Johnson',
    title: 'Certificate of excellence',
    course: 'Python for Beginners',
    completionDate: 'November 15, 2024',
    finalGrade: '96%',
    skills: ['Scratch basics', 'Loops', 'Functions', 'Debug'],
    type: 'excellence',
  },
  {
    id: '2',
    childName: 'Emma Johnson',
    title: 'Certificate of achievement',
    course: 'Scratch Programming',
    completionDate: 'October 22, 2024',
    finalGrade: '88%',
    skills: ['Scratch basics', 'Game design', 'Animation'],
    type: 'achievement',
  },
] as const

export const ParentCertificates: React.FC = () => {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return CERTIFICATES
    return CERTIFICATES.filter(c => {
      return (
        c.childName.toLowerCase().includes(q) ||
        c.course.toLowerCase().includes(q)
      )
    })
  }, [query])

  const totalCertificates = CERTIFICATES.length
  const excellenceCount = CERTIFICATES.filter(
    c => c.type === 'excellence'
  ).length
  const thisYear = CERTIFICATES.length

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Certificates</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and download your children&apos;s achievement certificates.
        </p>
      </div>

      {/* Summary chips */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="flex items-center justify-between rounded-2xl border-2 border-[#F7E0C9] bg-[#FDF7F1] px-5 py-4">
          <div>
            <p className="text-xs font-medium text-gray-500">
              Total certificates
            </p>
            <p className="mt-2 text-xl font-semibold text-gray-900">
              {totalCertificates}
            </p>
          </div>
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F28C28]/10 text-[#F28C28]">
            <Star className="h-5 w-5" />
          </span>
        </div>
        <div className="flex items-center justify-between rounded-2xl border-2 border-[#D0EAD9] bg-[#F4F8F7] px-5 py-4">
          <div>
            <p className="text-xs font-medium text-gray-500">
              Excellence awards
            </p>
            <p className="mt-2 text-xl font-semibold text-gray-900">
              {excellenceCount}
            </p>
          </div>
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <Star className="h-5 w-5" />
          </span>
        </div>
        <div className="flex items-center justify-between rounded-2xl border-2 border-[#C7D5E8] bg-[#F5F7F9] px-5 py-4">
          <div>
            <p className="text-xs font-medium text-gray-500">This year</p>
            <p className="mt-2 text-xl font-semibold text-gray-900">
              {thisYear}
            </p>
          </div>
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-50 text-sky-600">
            <Filter className="h-5 w-5" />
          </span>
        </div>
      </div>

      {/* Search bar */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.currentTarget.value)}
              placeholder="Search by child name or course"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-9 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition focus:border-portal-orange focus:bg-white focus:ring-1 focus:ring-portal-orange/40"
            />
          </div>
        </div>
      </div>

      {/* Certificates grid */}
      <div className="grid gap-5 lg:grid-cols-2">
        {filtered.map(cert => (
          <article
            key={cert.id}
            className="flex flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-[0_18px_40px_rgba(15,23,42,0.10)]"
          >
            {/* Gradient header */}
            <div className="bg-gradient-to-r from-[#F28C28] via-[#FFB347] to-[#22C55E] px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium opacity-90">
                    {cert.title}
                  </p>
                  <p className="text-sm font-semibold">{cert.childName}</p>
                  <p className="text-xs opacity-90">{cert.course}</p>
                </div>
                <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium">
                  {cert.type === 'excellence' ? 'Excellence' : 'Achievement'}
                </span>
              </div>
            </div>

            {/* Body */}
            <div className="space-y-4 px-6 py-5">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1 text-xs text-gray-500">
                  <p className="font-medium text-gray-700">
                    Completion Date
                  </p>
                  <p className="text-gray-900">{cert.completionDate}</p>
                </div>
                <div className="space-y-1 text-xs text-gray-500">
                  <p className="font-medium text-gray-700">Skills mastered</p>
                  <div className="flex flex-wrap gap-1.5">
                    {cert.skills.map(skill => (
                      <span
                        key={skill}
                        className="rounded-full bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-700"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="space-y-1 text-xs text-gray-500">
                  <p className="font-medium text-gray-700">Final grade</p>
                  <p className="text-gray-900">{cert.finalGrade}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
                <Button
                  type="button"
                  variant="primary"
                  className="rounded-xl bg-[#1D5AA6] px-4 py-2 text-xs font-semibold shadow-sm hover:bg-blue-800 focus:ring-blue-600"
                  icon={<Download className="h-4 w-4" />}
                >
                  Download PDF
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Share
                </Button>
              </div>
            </div>
          </article>
        ))}

        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
            No certificates found for this search yet.
          </div>
        )}
      </div>
    </div>
  )
}

